/**
 * Field cache helper functions for React Query.
 * Service functions for cache operations outside of React components.
 */

import { QueryClient, QueryKey, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import API from "common/utils/Api";
import { processFieldResponse } from "common/utils/helpers";
import { Field, FieldResponse } from "model/fieldTypes";
import type { MoveEvent } from "model/useAnimalGroupQueries";
import { invalidateFieldData } from "services/fieldOperations";

const fieldApi = new API<FieldResponse>("fields", "v2");

// Fields are pre-fetched during initialization and rarely change.
// Use a long staleTime to prevent unnecessary re-fetches when initialization
// is invoked multiple times during app load.
const FIELD_STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch multiple fields by their IDs and update React Query cache.
 * @param fieldIds - Array of field IDs to fetch
 * @param queryClient - React Query client instance
 * @returns Promise resolving to array of FieldResponse (raw API responses)
 */
export async function fetchFieldsByIdQuery(
  fieldIds: Array<string>,
  queryClient: QueryClient
): Promise<Array<FieldResponse>> {
  if (!fieldIds || fieldIds.length === 0) {
    return [];
  }

  // Fetch via React Query to get in-flight dedupe + staleTime caching.
  // This avoids duplicate network calls when farm init and hooks race on app load.
  const responses = await Promise.all(
    fieldIds.map((fieldId) =>
      queryClient.fetchQuery({
        queryKey: queryKeys.fields.detail(fieldId),
        queryFn: () => fieldApi.getOne(fieldId),
        staleTime: FIELD_STALE_TIME,
      })
    )
  );

  // Process the responses
  const processedFields = processFieldResponse(responses);

  // Update React Query cache for each field and handle validation
  processedFields.forEach((field) => {
    // Check if field was previously invalidated
    const cachedField = queryClient.getQueryData<Field>(
      queryKeys.fields.detail(field.guid)
    );

    // If field has tiles and was invalidated, validate it
    if (
      cachedField?.invalidatedTimestamp &&
      field.tiles &&
      field.tiles.length > 0
    ) {
      const {
        invalidatedTimestamp: _invalidatedTimestamp,
        ...fieldWithoutInvalidated
      } = field;
      queryClient.setQueryData<Field>(
        queryKeys.fields.detail(field.guid),
        fieldWithoutInvalidated as Field
      );
    } else {
      // Update cache with latest data
      queryClient.setQueryData<Field>(
        queryKeys.fields.detail(field.guid),
        field
      );
    }
  });

  // Return raw responses to match standard behavior
  return responses;
}

/**
 * Hook wrapper for fetchFieldsByIdQuery that automatically provides QueryClient
 */
export function useFetchFieldsByIdQuery() {
  const queryClient = useQueryClient();
  return async (fieldIds: Array<string>) =>
    fetchFieldsByIdQuery(fieldIds, queryClient);
}

/**
 * Change field visibility in React Query cache.
 * @param update - Object with guid and visibility properties
 * @param queryClient - React Query client instance
 */
export function changeFieldVisibilityQuery(
  update: {
    guid: string;
    visibility: "none" | "visible";
  },
  queryClient: QueryClient
): void {
  // Get current cached field data
  const currentField = queryClient.getQueryData<Field>(
    queryKeys.fields.detail(update.guid)
  );

  if (currentField) {
    // Update visibility in cached field
    queryClient.setQueryData<Field>(queryKeys.fields.detail(update.guid), {
      ...currentField,
      visibility: update.visibility,
    });
  }
}

/**
 * Hook wrapper for changeFieldVisibilityQuery that automatically provides QueryClient
 */
export function useChangeFieldVisibilityQuery() {
  const queryClient = useQueryClient();
  return (update: { guid: string; visibility: "none" | "visible" }) =>
    changeFieldVisibilityQuery(update, queryClient);
}

/**
 * Invalidate field data in React Query cache (mark field as needing re-fetch).
 * Sets invalidatedTimestamp, clears tiles array, and triggers a refetch.
 * @param fieldGuid - The field guid to invalidate
 * @param queryClient - React Query client instance
 */
export function invalidateFieldDataQuery(
  fieldGuid: string,
  queryClient: QueryClient
): void {
  invalidateFieldData(fieldGuid, queryClient);
}

/**
 * Hook wrapper for invalidateFieldDataQuery that automatically provides QueryClient
 */
export function useInvalidateFieldDataQuery() {
  const queryClient = useQueryClient();
  return (fieldGuid: string) =>
    invalidateFieldDataQuery(fieldGuid, queryClient);
}

/**
 * Youngest open move event for an animal group — mirrors the backend's
 * `findYoungestOpenForAnimalGroupGuid` (highest dateTimeIn, moveNumber tie-break).
 */
export function youngestOpenMoveEvent(
  moveEvents: MoveEvent[],
  animalGroupGuid: string
): MoveEvent | undefined {
  const openEvents = moveEvents.filter(
    (ev) =>
      ev.dateTimeOut === null &&
      ev.animalGroupGuid === animalGroupGuid &&
      ev.verificationStatus !== "Rejected"
  );

  return openEvents.reduce<MoveEvent | undefined>((acc, ev) => {
    if (!acc) return ev;
    const evTime = ev.dateTimeIn ? new Date(ev.dateTimeIn).getTime() : 0;
    const accTime = acc.dateTimeIn ? new Date(acc.dateTimeIn).getTime() : 0;
    if (evTime !== accTime) return evTime > accTime ? ev : acc;
    return ev.moveNumber > acc.moveNumber ? ev : acc;
  }, undefined);
}

/** Field guid where animals are grazing, from open move events (undefined if in shed). */
export function currentFieldGuidFromMoveEvents(
  moveEvents: MoveEvent[],
  animalGroupGuid: string
): string | undefined {
  const youngest = youngestOpenMoveEvent(moveEvents, animalGroupGuid);
  if (!youngest || youngest.movedToShed || !youngest.fieldGuid) {
    return undefined;
  }
  return youngest.fieldGuid;
}

function fieldGuidsToSyncForAnimalGroupLocation(
  queryClient: QueryClient,
  animalGroupGuid: string,
  moveEvents: MoveEvent[]
): Set<string> {
  const guids = new Set<string>();
  for (const ev of moveEvents) {
    if (ev.animalGroupGuid === animalGroupGuid && ev.fieldGuid) {
      guids.add(ev.fieldGuid);
    }
  }
  for (const [, field] of queryClient.getQueriesData<Field>({
    queryKey: queryKeys.fields.all,
  })) {
    if (!field) continue;
    if (
      field.animalGroupNow === animalGroupGuid ||
      field.animalGroup === animalGroupGuid
    ) {
      guids.add(field.guid);
    }
  }
  return guids;
}

/**
 * Align cached `animalGroupNow` with the youngest open move event. Used by
 * offline move optimism when multiple open events can coexist.
 */
export function syncAnimalGroupNowFromMoveEvents(
  queryClient: QueryClient,
  animalGroupGuid: string,
  moveEvents: MoveEvent[]
): Array<[QueryKey, unknown]> {
  const snapshot: Array<[QueryKey, unknown]> = [];
  const currentFieldGuid = currentFieldGuidFromMoveEvents(
    moveEvents,
    animalGroupGuid
  );
  const fieldGuids = fieldGuidsToSyncForAnimalGroupLocation(
    queryClient,
    animalGroupGuid,
    moveEvents
  );

  for (const fieldGuid of fieldGuids) {
    const key = queryKeys.fields.detail(fieldGuid);
    const field = queryClient.getQueryData<Field>(key);
    if (!field) continue;

    const nextAnimalGroupNow =
      fieldGuid === currentFieldGuid
        ? animalGroupGuid
        : field.animalGroupNow === animalGroupGuid
        ? undefined
        : field.animalGroupNow;

    if (nextAnimalGroupNow === field.animalGroupNow) continue;

    snapshot.push([key, field]);
    queryClient.setQueryData<Field>(key, {
      ...field,
      animalGroupNow: nextAnimalGroupNow,
    });
  }

  return snapshot;
}

/**
 * Optimistically update fields for move-in operation.
 * Sets animalGroupNow on new field and clears it on current field.
 * @param newFieldGuid - The field GUID where animals are moving to
 * @param queryClient - React Query client instance
 * @param currentFieldGuid - The field GUID where animals are moving from (optional)
 */
export function optimisticFieldMoveIn(
  newFieldGuid: string,
  queryClient: QueryClient,
  currentFieldGuid?: string
): void {
  // Get new field from cache
  const newField = queryClient.getQueryData<Field>(
    queryKeys.fields.detail(newFieldGuid)
  );

  if (currentFieldGuid) {
    // Get current field from cache
    const currentField = queryClient.getQueryData<Field>(
      queryKeys.fields.detail(currentFieldGuid)
    );

    // Validate that current and new fields have the same animal group
    if (
      newField &&
      currentField &&
      newField.animalGroup &&
      newField.animalGroup === currentField.animalGroup &&
      currentField.animalGroupNow === currentField.animalGroup
    ) {
      // Update new field: set animalGroupNow
      queryClient.setQueryData<Field>(queryKeys.fields.detail(newFieldGuid), {
        ...newField,
        animalGroupNow: newField.animalGroup,
      });

      // Update current field: clear animalGroupNow
      queryClient.setQueryData<Field>(
        queryKeys.fields.detail(currentFieldGuid),
        {
          ...currentField,
          animalGroupNow: undefined,
        }
      );
    }
  } else if (newField) {
    // No current field, just set animalGroupNow on new field
    queryClient.setQueryData<Field>(queryKeys.fields.detail(newFieldGuid), {
      ...newField,
      animalGroupNow: newField.animalGroup,
    });
  }
}

/**
 * Move animals into a field using React Query.
 * Optimistically updates fields and invalidates field queries for refetching.
 * @param currentFieldGuid - The field GUID where animals are moving from (optional)
 * @param newFieldGuid - The field GUID where animals are moving to
 * @param _dateTimeIn - The date/time when animals are moved in (kept for API compatibility)
 * @param queryClient - React Query client instance
 */
export async function moveIntoFieldQuery(
  currentFieldGuid: string | undefined,
  newFieldGuid: string,
  _dateTimeIn: Date,
  queryClient: QueryClient
): Promise<void> {
  // Optimistically update fields
  optimisticFieldMoveIn(newFieldGuid, queryClient, currentFieldGuid);

  // Invalidate field queries so they will be refetched after reading creation
  const fieldsToRefetch = [newFieldGuid];
  if (currentFieldGuid) {
    fieldsToRefetch.push(currentFieldGuid);
  }

  // Invalidate individual field queries
  await Promise.all(
    fieldsToRefetch.map((fieldGuid) =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.fields.detail(fieldGuid),
      })
    )
  );

  // Also invalidate the fields list query to ensure consistency
  await queryClient.invalidateQueries({ queryKey: queryKeys.fields.all });
}

/**
 * Validate field data in React Query cache (remove invalidatedTimestamp).
 * @param fieldGuid - The field guid to validate
 * @param queryClient - React Query client instance
 */
export function validateFieldDataQuery(
  fieldGuid: string,
  queryClient: QueryClient
): void {
  // Get current cached field data
  const currentField = queryClient.getQueryData<Field>(
    queryKeys.fields.detail(fieldGuid)
  );

  if (currentField) {
    // Remove invalidatedTimestamp from cached field
    const {
      invalidatedTimestamp: _invalidatedTimestamp,
      ...fieldWithoutInvalidated
    } = currentField;
    queryClient.setQueryData<Field>(
      queryKeys.fields.detail(fieldGuid),
      fieldWithoutInvalidated as Field
    );
  }
}

/**
 * Hook wrapper for validateFieldDataQuery that automatically provides QueryClient
 */
export function useValidateFieldDataQuery() {
  const queryClient = useQueryClient();
  return (fieldGuid: string) => validateFieldDataQuery(fieldGuid, queryClient);
}

/**
 * Reset all fields from React Query cache.
 * Removes all field-related queries from the cache.
 * @param queryClient - React Query client instance
 */
export function resetFieldsQueryCache(queryClient: QueryClient): void {
  // Remove all field-related queries from cache
  // With hierarchical keys, this removes all queries starting with ["fields"]
  queryClient.removeQueries({ queryKey: queryKeys.fields.all });
}

/**
 * Hook wrapper for resetFieldsQueryCache that automatically provides QueryClient
 */
export function useResetFieldsQueryCache() {
  const queryClient = useQueryClient();
  return () => resetFieldsQueryCache(queryClient);
}
